import { Instagram, Youtube, Facebook, MessageCircle } from "lucide-react";

/**
 * Centralized social platform config — used by Profile, PublicProfile, Friends,
 * and the Founder/Chef cards. Adding a new platform here propagates everywhere.
 *
 * Each platform exposes:
 *   key        — the user.profile.social_<key> field on the backend
 *   label      — input label / display label
 *   placeholder — input placeholder
 *   icon       — JSX node rendered next to the handle
 *   url(handle) — turns a stored handle into an absolute URL
 *   display(handle) — short text shown next to the icon
 */
export const SOCIAL_PLATFORMS = [
  {
    key: "social_instagram",
    label: "Instagram (handle, no @)",
    placeholder: "OmniMuscle_Bodybuilding",
    icon: <Instagram size={14} />,
    url: (h) => `https://instagram.com/${h.replace(/^@/, "")}`,
    display: (h) => `@${h.replace(/^@/, "")}`,
  },
  {
    key: "social_tiktok",
    label: "TikTok (handle, no @)",
    placeholder: "omnimuscle",
    icon: <span className="font-bold text-[11px]">TT</span>,
    url: (h) => `https://tiktok.com/@${h.replace(/^@/, "")}`,
    display: (h) => `@${h.replace(/^@/, "")}`,
  },
  {
    key: "social_youtube",
    label: "YouTube (handle or full URL)",
    placeholder: "@OmniMuscle",
    icon: <Youtube size={14} />,
    url: (h) => (h.startsWith("http") ? h : `https://youtube.com/${h.startsWith("@") ? h : "@" + h}`),
    display: (h) => h,
  },
  {
    key: "social_facebook",
    label: "Facebook (username or full URL)",
    placeholder: "omnimuscle",
    icon: <Facebook size={14} />,
    url: (h) => (h.startsWith("http") ? h : `https://facebook.com/${h.replace(/^@/, "")}`),
    display: (h) => h.replace(/^https?:\/\/(www\.)?facebook\.com\//i, "").replace(/^@/, ""),
  },
  {
    key: "social_threads",
    label: "Threads (handle, no @)",
    placeholder: "omnimuscle",
    icon: <span className="font-bold text-[11px]">@</span>,
    url: (h) => `https://www.threads.net/@${h.replace(/^@/, "")}`,
    display: (h) => `@${h.replace(/^@/, "")}`,
  },
  {
    key: "social_x",
    label: "X / Twitter (handle, no @)",
    placeholder: "omnimuscle",
    icon: <span className="font-bold text-[11px]">𝕏</span>,
    url: (h) => `https://x.com/${h.replace(/^@/, "")}`,
    display: (h) => `@${h.replace(/^@/, "")}`,
  },
  {
    key: "social_discord",
    label: "Discord (invite URL or username)",
    placeholder: "discord.gg/...",
    icon: <MessageCircle size={14} />,
    url: (h) => (h.startsWith("http") ? h : `https://discord.com/users/${h}`),
    display: (h) => (h.startsWith("http") ? "Discord →" : h),
  },
];

/**
 * SocialLinkRow — renders inline pill links for any user/founder/chef object
 * that has the SOCIAL_PLATFORMS keys at the top level.
 */
export function SocialLinkRow({ source, testIdPrefix = "social", className = "" }) {
  if (!source) return null;
  const visible = SOCIAL_PLATFORMS.filter((p) => source[p.key]);
  if (visible.length === 0) return null;
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      {visible.map((p) => {
        const handle = source[p.key];
        return (
          <a
            key={p.key}
            data-testid={`${testIdPrefix}-${p.key.replace("social_", "")}`}
            href={p.url(handle)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-neutral-300 hover:text-[#D4AF37] inline-flex items-center gap-1.5"
          >
            {p.icon} <span className="truncate max-w-[140px]">{p.display(handle)}</span>
          </a>
        );
      })}
    </div>
  );
}
